import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function JobApplicationForm({ jobId, jobTitle, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    coverLetter: '',
    resume: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check if user has already applied
  useEffect(() => {
    const checkApplicationStatus = async () => {
      const token = localStorage.getItem('candidateToken');
      if (!token) {
        setCheckingStatus(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/applications/check/${jobId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.applied) {
          setAlreadyApplied(true);
        }
      } catch (err) {
        console.error('Error checking application status:', err);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkApplicationStatus();
  }, [jobId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or Word document');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setFormData({ ...formData, resume: file });
      setResumeFileName(file.name);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate resume file
    if (!formData.resume) {
      setError('Please upload your CV/Resume');
      return;
    }

    const token = localStorage.getItem('candidateToken');
    if (!token) {
      setError('Please log in to apply for this job');
      return;
    }

    setLoading(true);

    try {
      // Create FormData object for file upload
      const data = new FormData();
      data.append('jobId', jobId);
      data.append('resume', formData.resume);
      if (formData.coverLetter) {
        data.append('coverLetter', formData.coverLetter);
      }

      const response = await axios.post(`${API_URL}/api/applications`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 201) {
        if (onSuccess) {
          onSuccess(response.data.application);
        }
      }
    } catch (err) {
      console.error('Application submission error:', err);
      if (err.response?.data?.alreadyApplied) {
        setError('You have already applied to this job');
        setAlreadyApplied(true);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to submit application. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div style={s.container}>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (alreadyApplied) {
    return (
      <div style={s.container}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
          <h3 style={{ fontSize: '20px', color: '#1a2f4d', marginBottom: '10px' }}>Already Applied</h3>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>
            You have already submitted an application for this job.
          </p>
          <a href="/dashboard" style={s.linkBtn}>View My Applications</a>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <h3 style={s.title}>Apply for {jobTitle}</h3>
      
      {error && (
        <div style={s.errorBox}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={s.form}>
        <div style={s.formGroup}>
          <label style={s.label}>
            Cover Letter <span style={{ color: '#6c757d', fontWeight: 400 }}>(Optional)</span>
          </label>
          <textarea
            value={formData.coverLetter}
            onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
            style={s.textarea}
            rows="6"
            placeholder="Tell us why you're a great fit for this role..."
          />
          <small style={{ fontSize: '12px', color: '#6c757d' }}>
            Mention your relevant experience and why you're interested in this position
          </small>
        </div>

        <div style={s.formGroup}>
          <label style={s.label}>
            CV/Resume <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <div style={s.fileUploadBox}>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="resume-upload"
            />
            <label htmlFor="resume-upload" style={s.fileUploadLabel}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>📄</div>
              {resumeFileName ? (
                <div style={{ color: '#28a745', fontWeight: 600 }}>
                  ✓ {resumeFileName}
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, color: '#1a2f4d', marginBottom: '5px' }}>
                    Click to upload CV/Resume
                  </div>
                  <div style={{ fontSize: '13px', color: '#6c757d' }}>
                    PDF or Word format (Max 5MB)
                  </div>
                </div>
              )}
            </label>
          </div>
        </div>

        <div style={s.buttonGroup}>
          {onCancel && (
            <button type="button" onClick={onCancel} style={s.cancelBtn}>
              Cancel
            </button>
          )}
          <button type="submit" disabled={loading} style={s.submitBtn}>
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  );
}

const s = {
  container: {
    background: '#fff',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginTop: '30px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1a2f4d',
    marginBottom: '20px',
    fontFamily: "'Montserrat', sans-serif",
  },
  errorBox: {
    padding: '12px 20px',
    background: '#fee',
    color: '#d33',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #fcc',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1a2f4d',
  },
  textarea: {
    padding: '12px 14px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: "'Open Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  fileUploadBox: {
    width: '100%',
  },
  fileUploadLabel: {
    display: 'block',
    padding: '30px 20px',
    border: '2px dashed #dee2e6',
    borderRadius: '8px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: '#f8f9fa',
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  cancelBtn: {
    padding: '12px 24px',
    background: '#fff',
    color: '#6c757d',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitBtn: {
    padding: '12px 32px',
    background: '#ff6b35',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  linkBtn: {
    display: 'inline-block',
    padding: '10px 20px',
    background: '#ff6b35',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
  },
};
